import { type NodeType } from '@tiptap/pm/model';
import { type Transaction } from '@tiptap/pm/state';

/**
 * Set a heading on the current selection, isolating a *partial* selection into
 * its own block first (Notion-style "turn into" on a sub-span). A heading is a
 * block, so it can't wrap part of a paragraph inline: when the selection is a
 * sub-range of a single textblock, the block is split before and after the
 * selection so only that middle block becomes the heading. A selection that
 * already covers a whole block (or is empty) converts that block directly.
 *
 * Mutates and returns `tr`. The caller guards that the selection is safe to
 * restyle (no section title, list, code block, or table cell) via
 * `selectionTouchesProtectedBlock`, so this only ever runs on paragraphs and
 * inline headings.
 */
export function setHeadingOnSelection(
  tr: Transaction,
  headingType: NodeType,
  level: number
): Transaction {
  const { from, to, empty } = tr.selection;
  const $from = tr.doc.resolve(from);
  const start = $from.start();
  const end = $from.end();
  const partialWithinBlock =
    !empty &&
    $from.sameParent(tr.doc.resolve(to)) &&
    $from.parent.isTextblock &&
    (from > start || to < end);

  if (!partialWithinBlock) {
    return tr.setBlockType(from, to, headingType, { level });
  }

  // Split off the trailing part first so `from` stays valid, then the leading
  // part. The selected text is left as its own block between the two splits.
  if (to < end) tr.split(to);
  if (from > start) tr.split(from);

  // Bias the mapped bounds inward so setBlockType targets only the middle block.
  return tr.setBlockType(tr.mapping.map(from, 1), tr.mapping.map(to, -1), headingType, {
    level,
  });
}
